import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  mapRentalsWithAdditionnalServices,
  TOAST_DURATION,
} from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { selectAttendeesWithInfosByHouseholds } from 'Store/booking/attendees/attendees.selector';
import {
  addOrReplaceAdditionalServices,
  deleteAdditionalServices,
} from 'Store/booking/proposals/proposals.action';
import { TRACK_BE_EVENT_GA_ACTIVITIES } from 'Tracking/events/be.tracking';
import { useBETracking } from 'Tracking/tracking-api/useBETracking';
import type { ServiceMutationType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { trackErrorOnAddService } from 'Views/booking/BookingEngine.utils';
import { RentalsBooking } from './RentalsBooking';
type Props = {
  proposals: ReadonlyArray<Proposal>;
  rentals: RentalsTree;
  selectedProposal: Proposal;
};
export const ProposalRentalsBooking = ({
  proposals,
  rentals,
  selectedProposal,
  ...rest
}: Props) => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const track = useBETracking();
  const { attendees: attendeesWithInfos } = useSelector(selectAttendeesWithInfosByHouseholds);
  useEffect(() => {
    let timer;

    if (updateInfo) {
      timer = setTimeout(() => setUpdateInfo(null), TOAST_DURATION);
    }

    return () => clearTimeout(timer);
  }, [updateInfo]);
  const contextValue = useMemo(
    () => ({
      attendeesList: [
        ...attendeesWithInfos.adults,
        ...attendeesWithInfos.children.map((child) => ({ ...child, isChild: true })),
      ],
      currency: selectedProposal.price.currency,
      hideAgeServiceSlot: true,
      setUpdateInfo,
      showRentAllSchedules: true,
      trackingData: TRACK_BE_EVENT_GA_ACTIVITIES,
      updateInfo,
      updateService: (
        attendees: ServiceMutationType,
        pricingInfo: PricingInfoWithScheduleType,
        serviceIds: ReadonlyArray<string>,
        attendeeIds: ReadonlyArray<string>,
      ) => {
        const info = {
          itemsNumber: attendees.length,
          message: attendees.length
            ? t('booking.activities.skiEquipment.newEquipmentAdded')
            : t('booking.activities.skiEquipment.newEquipmentRemoved'),
        };

        if (attendees.length === 0) {
          return dispatch(
            deleteAdditionalServices({
              showNotification: true,
              serviceIds,
              attendeeIds,
              appContext,
            }),
          ).then(() => setUpdateInfo(info));
        }

        return dispatch(
          addOrReplaceAdditionalServices({
            showNotification: true,
            attendees,
            appContext,
          }),
        )
          .then(() => setUpdateInfo(info))
          .catch((error) => trackErrorOnAddService(error, track, 'RENTAL_NOT_FOUND'));
      },
    }),
    [appContext, attendeesWithInfos, selectedProposal, updateInfo],
  );

  if (selectedProposal?.additionalServices?.rentals?.length === 0) {
    return null;
  }

  const rentalsWithAdditionnalServices = mapRentalsWithAdditionnalServices(
    rentals,
    selectedProposal.additionalServices.rentals,
    {
      filtered: true,
    },
  );
  return (
    <RentalsBooking
      {...rest}
      contextValue={contextValue}
      rentals={rentalsWithAdditionnalServices}
    />
  );
};
